
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { JsonLd, usePageSEO } from '../../components/seo/JsonLd';

export default function Services() {
  usePageSEO(
    'サービス | 株式会社SUNFIELD',
    '株式会社SUNFIELDの運送サービスをご紹介。一般貨物運送事業（3t・4tトラック）と軽貨物運送事業で関西圏全域に対応。安全管理体制と明確な料金体系でお客様をサポートします。',
    'サービス,一般貨物運送,軽貨物運送,3tトラック,4tトラック,関西圏,配送,料金'
  );

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "運送サービス",
    "description": "関西圏を中心とした一般貨物運送事業と軽貨物運送事業",
    "url": `${import.meta.env.VITE_SITE_URL}/services`,
    "provider": {
      "@type": "Organization",
      "name": "株式会社SUNFIELD"
    },
    "serviceType": "Transportation Services",
    "areaServed": [
      {
        "@type": "Place",
        "name": "京都府"
      },
      {
        "@type": "Place",
        "name": "大阪府"
      },
      {
        "@type": "Place",
        "name": "奈良県"
      },
      {
        "@type": "Place",
        "name": "兵庫県"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "運送サービス一覧",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "一般貨物運送事業",
            "description": "3t・4tトラックによる関西圏での確実な配送サービス"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "軽貨物運送事業",
            "description": "小回りの利く軽貨物車両での迅速な配送サービス"
          }
        }
      ]
    }
  };

  const services = [
    {
      icon: 'ri-truck-line',
      title: '一般貨物運送事業',
      description: '3t・4tトラックによる関西圏での確実な配送サービス',
      features: [
        '3t・4tトラックでの配送',
        '関西圏全域対応',
        '定期便・チャーター便',
        '企業間配送に特化'
      ],
      image: 'https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/85bbd8dbddb20ee6adc1f5791a0fd0ff.jpeg'
    },
    {
      icon: 'ri-car-line',
      title: '軽貨物運送事業',
      description: '小回りの利く軽貨物車両での迅速な配送サービス',
      features: [
        '軽貨物車両での配送',
        '小口配送に最適',
        '当日配送対応',
        'EC事業者様向け'
      ],
      image: 'Small delivery van in urban setting, professional commercial vehicle for light cargo transport, modern logistics service, clean business photography'
    }
  ];

  const safetyFeatures = [
    {
      icon: 'ri-shield-check-line',
      title: '安全管理体制',
      description: 'ドライバーの安全教育と車両の定期点検を徹底'
    },
    {
      icon: 'ri-time-line',
      title: '迅速対応',
      description: '若手チームの機動力で緊急配送にも柔軟に対応'
    },
    {
      icon: 'ri-customer-service-line',
      title: '24時間サポート',
      description: '配送状況の確認や緊急時の連絡体制を完備'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'GPS追跡',
      description: 'リアルタイムでの配送状況確認が可能'
    }
  ];

  const areas = [
    { name: '京都府', description: '京都市・宇治市・八幡市など全域' },
    { name: '大阪府', description: '大阪市・堺市・東大阪市など全域' },
    { name: '奈良県', description: '奈良市・橿原市・生駒市など全域' },
    { name: '兵庫県', description: '神戸市・尼崎市・西宮市など全域' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={serviceSchema} />
      <Header />
      
      {/* ヒーローセクション */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(30, 58, 138, 0.8)), url('https://public.readdy.ai/ai/img_res/e321ef07e149acf7c3ffe2d36e581cbc.jpg')`
        }}
      >
        <div className="text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">サービス</h1>
          <p className="text-xl text-blue-200">Services</p>
        </div>
      </section>

      {/* サービス概要 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              提供サービス
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              関西圏を中心とした一般貨物運送事業と軽貨物運送事業で、お客様のニーズに合わせた最適な物流ソリューションを提供いたします。
            </p>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-yellow-400`}></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-700">
                        <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/service-detail"
                    className="inline-flex items-center bg-blue-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    詳細を見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={index === 0 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/623e13e0b4ee49abbabb458e9e3bf567.jpeg" : index === 1 ? "https://static.readdy.ai/image/78304d8d02dcd7906feadaea6da56541/949d34f18ce70f53295c56f8e89be261.jpeg" : `https://readdy.ai/api/search-image?query=$%7Bservice.image%7D&width=600&height=400&seq=service-${index}&orientation=landscape`}
                    alt={service.title}
                    className="rounded-xl shadow-lg w-full h-auto object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 安全管理体制 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              安全管理体制
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              お客様の大切な荷物を安全・確実にお届けするため、徹底した安全管理体制を構築しています。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {safetyFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${feature.icon} text-2xl text-yellow-400`}></i>
                </div>
                <h3 className="text-lg font-bold text-blue-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 対応エリア */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              対応エリア
            </h2>
            <p className="text-xl text-gray-600">
              関西圏を中心に幅広いエリアでサービスを提供しています
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {areas.map((area, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
                <h3 className="text-xl font-bold text-blue-900 mb-3">{area.name}</h3>
                <p className="text-gray-700 text-sm">{area.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">
              上記以外のエリアについてもお気軽にご相談ください
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-map-pin-line mr-2"></i>
              エリア相談
            </Link>
          </div>
        </div>
      </section>

      {/* 料金体系 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
              料金体系
            </h2>
            <p className="text-xl text-gray-600">
              明確で分かりやすい料金設定でお客様をサポートします
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center">
                <i className="ri-truck-line mr-3 text-yellow-400"></i>
                一般貨物運送
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-700">基本料金（3t車）</span>
                  <span className="font-semibold text-blue-900">¥15,000〜</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-700">基本料金（4t車）</span>
                  <span className="font-semibold text-blue-900">¥18,000〜</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">時間料金</span>
                  <span className="font-semibold text-blue-900">¥3,000/h〜</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center">
                <i className="ri-car-line mr-3 text-yellow-400"></i>
                軽貨物運送
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-700">基本料金</span>
                  <span className="font-semibold text-blue-900">¥8,000〜</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                  <span className="text-gray-700">時間料金</span>
                  <span className="font-semibold text-blue-900">¥2,000/h〜</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">当日配送</span>
                  <span className="font-semibold text-blue-900">+¥2,000</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm mb-4">
              ※料金は荷物の内容、配送距離、時間帯により変動いたします
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-blue-900 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calculator-line mr-2"></i>
              お見積もり依頼
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            お客様のニーズに合わせた最適なプランをご提案
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            まずはお気軽にご相談ください。専門スタッフが丁寧にヒアリングし、最適な物流ソリューションをご提案いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:090-9801-4659"
              className="border-2 border-blue-900 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-blue-900 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              090-9801-4659
            </a>
            <Link
              to="/service-detail"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-text-line mr-2"></i>
              詳細資料
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
